import { Plan } from '@models/membership/Plan'
import { fetchPlans } from '@services/membership/PlanService'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { useEffect, useRef } from 'react'
import { SetURLSearchParams } from 'react-router-dom'

/**
 * Links from the Revenue page (and old bookmarks) scope a drill-down with
 * `f_programs`, which the single "Program / plan" tree cannot show - and which
 * `useTableUrlSync.handleFiltersChange` drops on the next edit, since it only
 * rewrites the form's fields. Once the plans are known, this turns the program
 * into its plan ids (`f_plans`) and removes `f_programs`, so the control shows
 * the scope and every later edit keeps it.
 *
 * Until then (or when the program has no plan) `f_programs` stays and keeps
 * filtering. Local to the two Revenue drill-downs: `useTableUrlSync` itself is
 * untouched.
 */
const useResolveProgramToPlans = (
  filters: Record<string, any>,
  setSearchParams: SetURLSearchParams
) => {
  const resolvedFor = useRef<string | undefined>(undefined)
  const programKey = ((filters.programs ?? []) as string[]).join(',')
  const hasPlans = ((filters.plans ?? []) as string[]).length > 0

  useEffect(() => {
    if (!programKey || hasPlans || resolvedFor.current === programKey) return
    resolvedFor.current = programKey

    fetchPlans({
      ...initialQuery,
      pagination: { current: 1, pageSize: 200 },
    })
      .then(({ data }) => {
        const programIds = programKey.split(',').map(Number)
        const planIds = ((data ?? []) as Plan[])
          .filter((plan) => programIds.includes(Number(plan.program?.id)))
          .map((plan) => plan.id)
        if (!planIds.length) return

        setSearchParams(
          (previous) => {
            previous.delete('f_programs')
            previous.delete('page')
            planIds.forEach((id) => previous.append('f_plans', String(id)))
            return previous
          },
          // The link and its scoped self are one page: do not trap Back.
          { replace: true }
        )
      })
      .catch((error) => {
        console.error('Error resolving the program to its plans:', error)
      })
  }, [programKey, hasPlans, setSearchParams])
}

export default useResolveProgramToPlans
